import { Component, ViewChild } from '@angular/core';
import {
    Frame, language, GenericFilter, template, DataTableComponent, date
} from 'curvy';
import { Task } from '@task-utils/types';
import { TaskRoutes } from '@task-utils/routes';
import { meters_translations } from './meters.trans';
import { TaskFilterComponent } from '@task-components/single-row-filter/single-row-filter.component';

type Meter = TaskRoutes.meters.Meter;
type MeterMeasure = TaskRoutes.meters.MeterMeasure;

interface MeterRow {
    meter: Meter;
    latest_date: string | null;
    recorded: boolean;
    cols: { vt: number | null; nt: number | null; snaga: number | null; plin: number | null; voda: number | null; };
}

enum MeterTypeName {
    ELECTRICITY = 'Električna energija',
    WATER = 'Voda',
    GAS = 'Plin',
}

enum MeterMeasureName {
    VT = 'Visoka tarifa',
    NT = 'Niska tarifa',
    POWER = 'Snaga',
}

const METER_TYPE_STYLE: Record<MeterTypeName, { icon: string; color: string }> = {
    [MeterTypeName.ELECTRICITY]: { icon: 'bolt', color: 'rgb(var(--yellow))' },
    [MeterTypeName.WATER]: { icon: 'opacity', color: 'rgb(var(--primary))' },
    [MeterTypeName.GAS]: { icon: 'local_fire_department', color: 'rgb(var(--orange))' },
};

const METER_TYPE_DEFAULT = { icon: 'av_timer', color: 'rgb(var(--background-dark))' };

const ELECTRICITY_COL_BY_MEASURE: Record<string, 'vt' | 'nt' | 'snaga'> = {
    [MeterMeasureName.VT]: 'vt',
    [MeterMeasureName.NT]: 'nt',
    [MeterMeasureName.POWER]: 'snaga',
};

type TableType = DataTableComponent<MeterRow, DisplayMeter, 'meters', TaskFilterComponent>;

@Component({
    selector: 'task-meters',
    template: `<knw-data-table #meterstable [config]="tableConfig"></knw-data-table>`
})
export class MetersComponent {
    @ViewChild('meterstable') table: TableType;

    constructor() {
        Frame.set({
            title: "NAVROUTE.METERS",
            visible: true,
            layout: "middle",
            size: "full"
        });
        language.load_translations(meters_translations);
    }

    tableConfig: TableType["tableConfig"] = {
        tableName: 'meters',
        getData: async (filter, paginator, sort_header) => {
            let parsed_filter: any = { search: [], attributes: [], sort: null };

            for (let key in filter) {
                if (filter[key] === null || filter[key] === undefined || filter[key] === "") continue;

                if (key == "location") {
                    parsed_filter.attributes.push("#=:location_id");
                    parsed_filter.search.push(filter[key].location_id);

                } else if (key == "meter_type_id") {
                    parsed_filter.attributes.push("#=:meter_type_id");
                    parsed_filter.search.push(filter[key]);

                } else if (key == "code") {
                    parsed_filter.attributes.push("*I:code");
                    parsed_filter.search.push(filter[key]);

                } else if (key == "reading_date_from") {
                    parsed_filter.attributes.push("-d:measurements.timestamp");
                    parsed_filter.search.push(date.start_of_day(filter[key]).toISOString());

                } else if (key == "reading_date_to") {
                    parsed_filter.attributes.push("+d:measurements.timestamp");
                    parsed_filter.search.push(date.end_of_day(filter[key]).toISOString());

                } else if (key == "servicer") {
                    parsed_filter.attributes.push("#=:measurements.created_by_id");
                    parsed_filter.search.push(filter[key].user_id);

                } else {
                    parsed_filter.attributes.push(key);
                    parsed_filter.search.push(filter[key]);
                }
            }

            parsed_filter.sort = sort_header;

            let res = await TaskRoutes.meters.api_meter_search(parsed_filter, paginator);
            let rows = (res.data || []).map((meter: Meter) => build_row(meter));

            return { ...res, data: rows } as any;
        },
        getHeader: (header) => {
            switch (header) {
                case 'date':
                    return { icon: "event", label: "METERS.DATE", sortable: false };

                case 'type':
                    return { icon: "category", label: "METERS.TYPE", sortable: false };

                case 'serial':
                    return { icon: "tag", label: "METERS.SERIAL", sortable: false };

                case 'location':
                    return { icon: "place", label: "METERS.LOCATION", sortable: false };

                case 'recorded':
                    return { icon: "fact_check", label: "METERS.READ", sortable: false };

                case 'vt':
                    return { icon: "arrow_upward", label: "METERS.VT", sortable: false };

                case 'nt':
                    return { icon: "arrow_downward", label: "METERS.NT", sortable: false };

                case 'power':
                    return { icon: "bolt", label: "METERS.POWER", sortable: false };

                case 'gas':
                    return { icon: "local_fire_department", label: "METERS.GAS_VAL", sortable: false };

                case 'water':
                    return { icon: "opacity", label: "METERS.WATER_VAL", sortable: false };
            }
        },
        unwrapData: (row) => {
            let num = (v: number | null) => (v === null || v === undefined) ? "–" : `${v}`;
            let meter = row.meter;

            return {
                date: row.latest_date
                    ? template.transform(`<span style="white-space:nowrap">{{ d | date << short_date }}</span>`, { d: new Date(row.latest_date) })
                    : template.transform(`<span style="opacity:.4">–</span>`, {}),

                type: template.transform(`
                    <div class="flex-column" style="width: 100%; text-align: left;">
                        <div class="flex-row align-center" style="gap: .4em;">
                            <i style="color: {{ col }}">{{ ic | icon }}</i>
                            <span>{{ name }}</span>
                        </div>
                    </div>
                `, { col: meter_type_color(meter.meter_type_name), ic: meter_type_icon(meter.meter_type_name), name: meter.meter_type_name }),

                serial: template.transform(`<span>{{ c }}</span>`, { c: meter.code }),

                location: template.transform(`
                    <div class="flex-column align-flex-start">
                        <span style="text-align:left">{{ name }}</span>
                        <span style="font-size:.85em; opacity:.7">{{ tag }}</span>
                    </div>
                `, { name: meter.location?.name ?? '', tag: meter.location?.internal_tag ?? '' }),

                recorded: row.recorded
                    ? template.transform(`<i style="color: #5a5">{{ ic | icon }}</i>`, { ic: "check_circle" })
                    : template.transform(`<i style="opacity:.4">{{ ic | icon }}</i>`, { ic: "remove" }),

                vt: template.transform(`<span>{{ v }}</span>`, { v: num(row.cols.vt) }),
                nt: template.transform(`<span>{{ v }}</span>`, { v: num(row.cols.nt) }),
                power: template.transform(`<span>{{ v }}</span>`, { v: num(row.cols.snaga) }),
                gas: template.transform(`<span>{{ v }}</span>`, { v: num(row.cols.plin) }),
                water: template.transform(`<span>{{ v }}</span>`, { v: num(row.cols.voda) }),
            };
        },
        showHeaderWhenEmpty: true,
        maxActions: 0,
        rowActions: [],
        filterComponent: TaskFilterComponent,
        filterInput: async () => {
            let type_opts = TaskRoutes.meters.api_meter_type_get_all({ page_no: 1, page_size: 100 })
                .then(tres => (tres.data || []).map((t: TaskRoutes.meters.MeterType) => ({ label: t.name, value: t.meter_type_id })))
                .catch(() => [] as { label: string, value: any }[]);

            let filters: TaskFilterComponent["filterInput"] = [
                GenericFilter.search("METERS.LOCATION", "location", this.search_locations, this.format_location_name),
                GenericFilter.dropdown("METERS.TYPE", "meter_type_id", type_opts),
                GenericFilter.text("METERS.SERIAL", "code"),
                GenericFilter.search("METERS.SERVICER", "servicer", this.search_users, this.format_user_name),
                GenericFilter.date_filter("METERS.READING_FROM", "reading_date_from"),
                GenericFilter.date_filter("METERS.READING_TO", "reading_date_to"),
            ];

            return filters;
        }
    }

    async search_locations(search: string) {
        let res = await TaskRoutes.locations.api_location_search_visible(
            { search: [search], attributes: ["cin+ +name"] },
            { page_no: 1, page_size: 50 }
        );
        return res.data;
    }

    format_location_name(loc: Task.Location) {
        if (!loc) { return ""; }
        return `${loc.cin ?? ''} ${loc.name}`;
    }

    async search_users(search: string) {
        let res = await TaskRoutes.users.api_user_search({ search: [search], attributes: ["first_name+ +last_name"] });
        return res.data;
    }

    format_user_name(usr: Task.User) {
        if (!usr) { return ""; }
        return `${usr.first_name} ${usr.last_name}`;
    }
}

function meter_type_style(type_name: string) {
    return METER_TYPE_STYLE[type_name as MeterTypeName] ?? METER_TYPE_DEFAULT;
}

function meter_type_color(type_name: string) {
    return meter_type_style(type_name).color;
}

function meter_type_icon(type_name: string) {
    return meter_type_style(type_name).icon;
}

function build_row(meter: Meter): MeterRow {
    let measures = meter.measures || [];

    let latest_date: string | null = null;
    for (let m of measures) {
        if (m.timestamp && (latest_date === null || m.timestamp > latest_date)) {
            latest_date = m.timestamp;
        }
    }

    let recorded = measures.length > 0 && measures.every(m => m.value !== null && m.value !== undefined);

    return { meter, latest_date, recorded, cols: classify(meter) };
}

function classify(meter: Meter): MeterRow['cols'] {
    let cols: MeterRow['cols'] = { vt: null, nt: null, snaga: null, plin: null, voda: null };
    let measures = meter.measures || [];
    let value_of = (m?: MeterMeasure) => m ? (m.value ?? null) : null;

    let type_name = meter.meter_type_name;

    if (type_name === MeterTypeName.ELECTRICITY || measures.length >= 3) {
        for (let m of measures) {
            let col = ELECTRICITY_COL_BY_MEASURE[(m.name || '').trim()];
            if (col) { cols[col] = value_of(m); }
        }
        if (cols.vt === null && cols.nt === null && cols.snaga === null) {
            cols.vt = value_of(measures[0]);
            cols.nt = value_of(measures[1]);
            cols.snaga = value_of(measures[2]);
        }
    } else if (type_name === MeterTypeName.GAS) {
        cols.plin = value_of(measures[0]);
    } else if (type_name === MeterTypeName.WATER) {
        cols.voda = value_of(measures[0]);
    }

    return cols;
}

interface DisplayMeter {
    date: template.EscapedHTML;
    type: template.EscapedHTML;
    serial: template.EscapedHTML;
    location: template.EscapedHTML;
    recorded: template.EscapedHTML;
    vt: template.EscapedHTML;
    nt: template.EscapedHTML;
    power: template.EscapedHTML;
    gas: template.EscapedHTML;
    water: template.EscapedHTML;
}
